import React, { useState } from "react";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabContent from "../../../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs, { KuikaTabsSize } from "../../../../ui-kit/kuika-tabs/kuika-tabs";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";

interface ITabOption {
  value: string;
  label?: string;
  icon?: React.ReactNode;
}

interface IStatefulProps {
  options: (string | ITabOption)[];
  size?: KuikaTabsSize;
  disabled?: boolean;
  initial?: string;
}

const Stateful: React.FC<IStatefulProps> = ({ options, initial, ...rest }: IStatefulProps): JSX.Element => {
  const firstValue: string = typeof options[0] === "string" ? options[0] : options[0].value;
  const [value, setValue] = useState<string>(initial ?? firstValue);
  return <KuikaTabs options={options} value={value} onChange={(next: string): void => setValue(next)} {...rest} />;
};

const PANE_TABS: string[] = ["design", "build", "deploy"];

interface IPaneCopy {
  title: string;
  lines: string[];
}

const PANES: Record<string, IPaneCopy> = {
  design: { title: "Design", lines: ["Screens, components and styles.", "Drag components onto the canvas."] },
  build: { title: "Build", lines: ["Actions, datasources and workflows."] },
  deploy: {
    title: "Deploy",
    lines: ["Environments and releases.", "Pick a target, review the changes,", "then publish."]
  }
};

interface IContentTransitionProps {
  hasOrder: boolean;
  /** Every pane stays mounted with a field in it, as tabs sharing one form do. */
  keepMounted?: boolean;
}

const ContentTransition: React.FC<IContentTransitionProps> = ({
  hasOrder,
  keepMounted = false
}: IContentTransitionProps): JSX.Element => {
  const [tab, setTab] = useState<string>(PANE_TABS[0]);

  return (
    <div style={{ width: 360, display: "flex", flexDirection: "column", gap: "var(--gap-m)" }}>
      <KuikaTabs
        options={PANE_TABS.map((value: string): ITabOption => ({ value, label: PANES[value].title }))}
        value={tab}
        onChange={(next: string): void => setTab(next)}
      />
      <KuikaTabContent activeKey={tab} order={hasOrder ? PANE_TABS : undefined} keepMounted={keepMounted}>
        {PANE_TABS.map((value: string): JSX.Element => (
          <div key={value} style={{ border: "1px solid var(--lab-border)", padding: "var(--gap-m)" }}>
            <strong>{PANES[value].title}</strong>
            {PANES[value].lines.map((line: string): JSX.Element => (
              <p key={line} style={{ margin: 0 }}>
                {line}
              </p>
            ))}
            {keepMounted && <KuikaInput size="md" placeholder={`${PANES[value].title} notes — kept across tabs`} />}
          </div>
        ))}
      </KuikaTabContent>
    </div>
  );
};

export const TabsStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="Tabs"
      componentName="KuikaTabs"
      sourcePath="ui-kit/kuika-tabs"
      description="Segmented control on the control ladder: sm (24px option, 28px control — the default) and lg (32px control, to line up with a 32px input). The pill slides on the layout spring; pair it with KuikaTabContent to swap the content below."
    >
      <SpecRow label="Size · sm (default)" caption="24px option, 28px control" align="start">
        <Stateful options={["Design", "Build", "Deploy"]} />
      </SpecRow>
      <SpecRow label="Size · lg" caption="28px option, 32px control" align="start">
        <Stateful size="lg" options={["Design", "Build", "Deploy"]} />
      </SpecRow>
      <SpecRow label="With icons" align="start">
        <Stateful
          options={[
            { value: "design", label: "Design", icon: <KuikaSvg name="brush" width={14} height={14} /> },
            { value: "build", label: "Build", icon: <KuikaSvg name="hammer" width={14} height={14} /> },
            { value: "deploy", label: "Deploy", icon: <KuikaSvg name="rocket" width={14} height={14} /> }
          ]}
        />
      </SpecRow>
      <SpecRow label="Disabled">
        <Stateful options={["Design", "Build", "Deploy"]} disabled />
      </SpecRow>
      <SpecRow
        label="Content transition"
        caption="KuikaTabContent — the old pane leaves over the new one; the height follows at once"
        align="start"
      >
        <ContentTransition hasOrder={false} />
      </SpecRow>
      <SpecRow
        label="Content transition · order"
        caption="order given — panes travel towards the new tab"
        align="start"
      >
        <ContentTransition hasOrder />
      </SpecRow>
      <SpecRow
        label="Content transition · keepMounted"
        caption="every pane stays mounted (the inactive ones hidden), so a field keeps what was typed; no leaver — the new pane only comes in"
        align="start"
      >
        <ContentTransition hasOrder keepMounted />
      </SpecRow>
    </ShowcaseFrame>
  );
};
